import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import breakpoint from 'styled-components-breakpoint';

import { hasCallToAction } from '../utils';
import Cross from '../assets/images/cross-dark.svg';

import Block from './Block';
import H2 from './H2';
import RichTextHtml from './RichTextHtml';
import Cell from './Cell';
import Grid from './Grid';
import CTAAnchor from './CTAAnchor';
import Image from './Image';
import H4 from './H4';
import Button from './Button';

const LogosGrid = styled(Block)`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${({ theme }) => theme.spacing.baseUnit};
  align-items: start;

  ${breakpoint('smallTablet')`
    grid-template-columns: repeat(3, 1fr);
  `}

  ${breakpoint('tablet')`
    grid-template-columns: repeat(4, 1fr);
  `}
`;

const LogoBlock = styled(Block)`
  box-shadow: ${({ isOpen }) =>
    isOpen ? '0' : '0 1px 4px rgba(0, 0, 0, 0.15)'};
  border-radius: ${({ theme }) => theme.spacing.baseUnit};
  order: ${({ order }) => order};
  cursor: pointer;
  transition: all 0.2s;

  &:active {
    transform: scale(1.1);
  }
`;

const DescriptionBlock = styled(Block)`
  grid-column: 1 / 1;
  order: ${({ order }) => (order || 1) + 1};

  ${breakpoint('smallTablet')`
    grid-column: 1 / 4;
    order: ${({ order }) => (Math.ceil(order / 3) || 1) * 3 + 1};
    max-width: 750px;
    margin-left: auto;
    margin-right: auto;
  `}

  ${breakpoint('tablet')`
    grid-column: 1 / 5;
    order: ${({ order }) => (Math.ceil(order / 4) || 1) * 4 + 1};
    max-width: 850px;
    
  `};
`;

const ItemType = PropTypes.shape({
  collaboration_title: PropTypes.string.isRequired,
  description: PropTypes.shape({ html: PropTypes.string }).isRequired,
  logo: PropTypes.shape({}).isRequired,
  call_to_action_color: PropTypes.string,
  call_to_action_text: PropTypes.shape({ text: PropTypes.string }),
  call_to_action_link: PropTypes.shape({
    url: PropTypes.string,
    link_type: PropTypes.string,
    lang: PropTypes.string,
    target: PropTypes.string,
  }),
});

const LogosGridItem = ({ item, isOpen, onClick }) => (
  <>
    <LogoBlock order={item.order} isOpen={isOpen} onClick={onClick}>
      <Block p="baseUnit">
        <Image content={item.logo} />
      </Block>
    </LogoBlock>

    {isOpen && (
      <DescriptionBlock
        p="baseUnit"
        px={{ smallTablet: 'baseUp8' }}
        order={item.order}
      >
        <Block
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb="baseDown2"
        >
          <H4 color="blueDark">{item.collaboration_title}</H4>

          <Button
            backgroundColor="transparent"
            hoverColor="transparent"
            pt="baseDown3"
            pb="baseDown2"
            pr="baseDown3"
            onClick={onClick}
          >
            <img src={Cross} alt="close-description" />
          </Button>
        </Block>

        <RichTextHtml html={item.description.html} />

        {hasCallToAction(item) ? (
          <Block pt="baseUp4">
            <CTAAnchor
              text={item.call_to_action_text.text}
              link={item.call_to_action_link}
              colorTheme={item.call_to_action_color}
            />
          </Block>
        ) : null}
      </DescriptionBlock>
    )}
  </>
);

LogosGridItem.propTypes = {
  item: { ...ItemType, order: PropTypes.number.isRequired }.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

const PageCollaborationPartners = ({ content }) => {
  const [activeCollabId, setActiveCollabId] = useState('');
  const toggleCollab = collabId => {
    setActiveCollabId(val => (val === collabId ? '' : collabId));
  };

  return (
    <Block
      id={content.primary.internal_link_id}
      py={{ mobile: 'baseUp4', tablet: 'baseUp12' }}
    >
      <Grid>
        <Cell size="1">
          <Block
            px="baseUnit"
            py={{ mobile: 'baseDown2', tablet: '0' }}
            pl="baseUp4"
          >
            {content.primary.title1 && content.primary.title1.text && (
              <H2 mb="baseUnit">{content.primary.title1.text}</H2>
            )}

            <LogosGrid>
              {content.items.map((item, i) => {
                const collabId = `${item.collaboration_title}-${i}`;
                const isOpen = activeCollabId === collabId;

                return (
                  <LogosGridItem
                    key={`logo-item-${collabId}`}
                    item={{ ...item, order: i + 1 }}
                    isOpen={isOpen}
                    onClick={() => toggleCollab(collabId)}
                  />
                );
              })}
            </LogosGrid>
          </Block>
        </Cell>
      </Grid>
    </Block>
  );
};

PageCollaborationPartners.propTypes = {
  content: PropTypes.shape({
    primary: PropTypes.shape({
      internal_link_id: PropTypes.string,
      title1: PropTypes.shape({ text: PropTypes.string }),
    }),
    items: PropTypes.arrayOf(ItemType),
  }).isRequired,
};

export default PageCollaborationPartners;

export const query = graphql`
  fragment PageCollaborationPartnersFragment on PrismicPageBodyCollaborationPartners {
    id
    slice_type
    primary {
      internal_link_id
      title1 {
        text
      }
    }
    items {
      collaboration_title
      description {
        html
      }
      logo {
        alt
        localFile {
          url
          childImageSharp {
            fluid(maxWidth: 550) {
              ...GatsbyImageSharpFluid
            }
          }
        }
      }
      call_to_action_color
      call_to_action_text {
        text
      }
      call_to_action_link {
        link_type
        url
        lang
        target
      }
    }
  }
`;
